import React, { createContext, useContext, useEffect, useState } from 'react'
import { graphql, useStaticQuery } from 'gatsby'
import { fetchPricesFromGoogleSheet } from '../services/googleSheetsPrices'

type PricesContextType = {
  prices: Price[]
  prices2: Price[]
  prices3: Price[]
  isLive: boolean
  lastUpdated: Date | null
}

export const PricesContext = createContext<PricesContextType>({
  prices: [],
  prices2: [],
  prices3: [],
  isLive: false,
  lastUpdated: null,
})

export const PricesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const staticData = useStaticQuery<{
    preciosYaml: {
      prices: Price[]
      prices2: Price[]
      prices3: Price[]
    }
  }>(graphql`
    query AllPricesStaticFallback {
      preciosYaml {
        prices {
          key
          text
          price
          promo
          promo_long
        }
        prices2 {
          key
          text
          price
          promo
          promo_long
        }
        prices3 {
          key
          text
          price
          promo
          promo_long
        }
      }
    }
  `)

  const fallback = staticData?.preciosYaml || {}
  const [prices, setPrices] = useState<Price[]>(fallback.prices || [])
  const [prices2, setPrices2] = useState<Price[]>(fallback.prices2 || [])
  const [prices3, setPrices3] = useState<Price[]>(fallback.prices3 || [])
  const [isLive, setIsLive] = useState(false)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

const STORAGE_KEY = 'guardalo_cached_prices_v1'

interface CachedPricesPayload {
  prices: Price[]
  prices2: Price[]
  prices3: Price[]
  timestamp: number
}

  useEffect(() => {
    // 1. Immediately hydrate from localStorage if available
    try {
      if (typeof window !== 'undefined') {
        const cachedStr = window.localStorage.getItem(STORAGE_KEY)
        if (cachedStr) {
          const cached: CachedPricesPayload = JSON.parse(cachedStr)
          let hasCached = false
          if (Array.isArray(cached.prices) && cached.prices.length > 0) {
            setPrices(cached.prices)
            hasCached = true
          }
          if (Array.isArray(cached.prices2) && cached.prices2.length > 0) {
            setPrices2(cached.prices2)
            hasCached = true
          }
          if (Array.isArray(cached.prices3) && cached.prices3.length > 0) {
            setPrices3(cached.prices3)
            hasCached = true
          }
          if (hasCached) {
            setIsLive(true)
            if (cached.timestamp) {
              setLastUpdated(new Date(cached.timestamp))
            }
          }
        }
      }
    } catch (e) {
      console.warn('Could not read cached prices from localStorage:', e)
    }

    // 2. Fetch fresh prices from Google Sheets
    const sheetUrl = process.env.GATSBY_PRICES_SHEET_URL
    if (!sheetUrl) {
      return
    }

    fetchPricesFromGoogleSheet(sheetUrl)
      .then((live) => {
        let updated = false
        if (live.prices.length > 0) {
          setPrices(live.prices)
          updated = true
        }
        if (live.prices2.length > 0) {
          setPrices2(live.prices2)
          updated = true
        }
        if (live.prices3.length > 0) {
          setPrices3(live.prices3)
          updated = true
        }
        if (updated) {
          setIsLive(true)
          const now = new Date()
          setLastUpdated(now)
          try {
            if (typeof window !== 'undefined') {
              window.localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({
                  prices: live.prices,
                  prices2: live.prices2,
                  prices3: live.prices3,
                  timestamp: now.getTime(),
                })
              )
            }
          } catch (storageErr) {
            console.warn('Could not save prices to localStorage:', storageErr)
          }
        }
      })
      .catch((err) => {
        console.warn('Google Sheets live prices could not be loaded, using cached or static fallback:', err)
      })
  }, [])

  return (
    <PricesContext.Provider value={{ prices, prices2, prices3, isLive, lastUpdated }}>
      {children}
    </PricesContext.Provider>
  )
}

export const usePricesContext = () => useContext(PricesContext)
